/**
 * 浮动通知动画组件
 * 用于显示各种浮动提示信息，如经验值、成就、提示等
 */

import SoundEffects from '@/utils/soundEffects';

// 预设主题配置
const PRESET_THEMES = {
  // 经验值主题 - 绿色
  exp: {
    background: 'rgba(255, 255, 255, 0.96)',
    shadowColor: 'rgba(82, 196, 26, 0.18)',
    icon: '✦',
    color: '#1f2d3d',
    borderColor: 'rgba(82, 196, 26, 0.22)',
    iconBackground: 'rgba(82, 196, 26, 0.12)',
    iconColor: '#389e0d'
  },
  // 连续学习主题 - 橙色
  streak: {
    background: 'rgba(255, 255, 255, 0.96)',
    shadowColor: 'rgba(255, 169, 64, 0.18)',
    icon: '🔥',
    color: '#1f2d3d',
    borderColor: 'rgba(255, 169, 64, 0.26)',
    iconBackground: 'rgba(255, 169, 64, 0.15)',
    iconColor: '#d97706'
  },
  // 成功主题 - 蓝色
  success: {
    background: 'linear-gradient(135deg, #1890ff, #36cfc9)',
    shadowColor: 'rgba(24, 144, 255, 0.5)',
    icon: '✓',
    color: 'white'
  },
  // 警告主题 - 黄色
  warning: {
    background: 'linear-gradient(135deg, #faad14, #ffc53d)',
    shadowColor: 'rgba(250, 173, 20, 0.5)',
    icon: '⚠',
    color: 'white'
  },
  // 错误主题 - 红色
  error: {
    background: 'linear-gradient(135deg, #ff4d4f, #ff7875)',
    shadowColor: 'rgba(255, 77, 79, 0.5)',
    icon: '✕',
    color: 'white'
  },
  // 信息主题 - 紫色
  info: {
    background: 'linear-gradient(135deg, #722ed1, #9254de)',
    shadowColor: 'rgba(114, 46, 209, 0.5)',
    icon: 'ℹ',
    color: 'white'
  },
  // 升级主题 - 金色
  levelUp: {
    background: 'linear-gradient(135deg, #fadb14, #ffd666)',
    shadowColor: 'rgba(250, 219, 20, 0.6)',
    icon: '⭐',
    color: '#333'
  }
};

// 动画位置配置
const POSITIONS = {
  bottomRight: { right: '40px', bottom: '40px' },
  bottomLeft: { left: '40px', bottom: '40px' },
  topRight: { right: '40px', top: '40px' },
  topLeft: { left: '40px', top: '40px' },
  middleRight: { right: '28px', top: '50%' },
  center: { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }
};

// 动画方向配置
const ANIMATIONS = {
  floatUp: {
    initial: { opacity: 0, transform: 'translateY(0) scale(0.5)' },
    show: { opacity: 1, transform: 'translateY(-30px) scale(1)' },
    hide: { opacity: 0, transform: 'translateY(-80px) scale(0.9)' }  // 减少移动距离
  },
  floatDown: {
    initial: { opacity: 0, transform: 'translateY(0) scale(0.5)' },
    show: { opacity: 1, transform: 'translateY(30px) scale(1)' },
    hide: { opacity: 0, transform: 'translateY(80px) scale(0.9)' }  // 减少移动距离
  },
  fadeIn: {
    initial: { opacity: 0, transform: 'scale(0.5)' },
    show: { opacity: 1, transform: 'scale(1)' },
    hide: { opacity: 0, transform: 'scale(0.8)' }
  },
  slideRight: {
    initial: { opacity: 0, transform: 'translateX(-100px) scale(0.8)' },
    show: { opacity: 1, transform: 'translateX(0) scale(1)' },
    hide: { opacity: 0, transform: 'translateX(100px) scale(0.8)' }
  },
  bounce: {
    initial: { opacity: 0, transform: 'translateY(0) scale(0.3)' },
    show: { opacity: 1, transform: 'translateY(-50px) scale(1.2)' },
    hide: { opacity: 0, transform: 'translateY(-100px) scale(0.5)' }
  }
};

/**
 * 创建浮动通知
 * @param {Object} options - 配置选项
 * @param {string} options.text - 显示的文本内容（必填）
 * @param {string} [options.theme='exp'] - 主题名称，可选值见 PRESET_THEMES
 * @param {string} [options.shape='circle'] - 形状：'circle'（圆形）, 'rounded'（圆角矩形）, 'square'（方形）
 * @param {string} [options.size='medium'] - 大小：'small', 'medium', 'large'
 * @param {string} [options.position='bottomRight'] - 位置，可选值见 POSITIONS
 * @param {string} [options.animation='floatUp'] - 动画类型，可选值见 ANIMATIONS
 * @param {number} [options.duration=2000] - 持续时间（毫秒）
 * @param {number} [options.zIndex=9999] - z-index 层级
 * @param {Object} [options.custom] - 自定义样式覆盖
 * @param {string} [options.custom.background] - 自定义背景
 * @param {string} [options.custom.color] - 自定义文字颜色
 * @param {string} [options.custom.icon] - 自定义图标
 * @param {string} [options.custom.fontSize] - 自定义字体大小
 * @param {boolean} [options.playSound=true] - 是否播放音效
 * @param {Function} [options.onShow] - 显示时的回调
 * @param {Function} [options.onHide] - 隐藏时的回调
 * @param {Function} [options.onClick] - 点击时的回调
 */
export function createFloatingNotification(options) {
  // 参数验证
  if (!options || !options.text) {
    console.error('FloatingNotification: text is required');
    return null;
  }

  // 默认配置
  const config = {
    theme: 'exp',
    shape: 'circle',
    size: 'medium',
    position: 'bottomRight',
    animation: 'floatUp',
    layout: 'default',
    duration: 2000,
    zIndex: 9999,
    playSound: true,
    custom: {},
    ...options
  };

  // 播放对应的音效
  if (config.playSound) {
    const soundMap = {
      exp: () => SoundEffects.playExp(),
      streak: () => SoundEffects.playStreak(),
      success: () => SoundEffects.playSuccess(),
      warning: () => SoundEffects.playWarning(),
      error: () => SoundEffects.playError(),
      info: () => SoundEffects.playInfo(),
      levelUp: () => SoundEffects.playLevelUp()
    };

    const playSound = soundMap[config.theme];
    if (playSound) {
      playSound();
    } else {
      SoundEffects.playNotification();
    }
  }

  // 获取主题配置
  const themeConfig = PRESET_THEMES[config.theme] || PRESET_THEMES.exp;
  
  // 获取位置配置
  const positionConfig = POSITIONS[config.position] || POSITIONS.bottomRight;
  
  // 获取动画配置
  const animationConfig = ANIMATIONS[config.animation] || ANIMATIONS.floatUp;

  // 尺寸配置
  const sizeMap = {
    small: { width: '100px', height: '100px', fontSize: '14px', padding: '20px 30px' },
    medium: { width: '120px', height: '120px', fontSize: '16px', padding: '30px 40px' },
    large: { width: '150px', height: '150px', fontSize: '20px', padding: '40px 50px' },
    compact: { width: 'auto', height: 'auto', fontSize: '14px', padding: '10px 14px', minWidth: '0' }
  };
  const sizeConfig = sizeMap[config.size] || sizeMap.medium;

  // 形状配置
  const shapeMap = {
    circle: '50%',
    rounded: '12px',
    square: '4px'
  };
  const borderRadius = shapeMap[config.shape] || shapeMap.circle;

  const element = document.createElement('div');

  if (config.layout === 'compactPill') {
    const iconEl = document.createElement('span');
    iconEl.textContent = config.custom.icon || themeConfig.icon || '';
    iconEl.style.cssText = [
      'width: 24px',
      'height: 24px',
      'display: inline-flex',
      'align-items: center',
      'justify-content: center',
      'flex-shrink: 0',
      'border-radius: 8px',
      `background: ${themeConfig.iconBackground || 'rgba(0, 0, 0, 0.06)'}`,
      `color: ${themeConfig.iconColor || config.custom.color || themeConfig.color}`,
      'font-size: 13px',
      'line-height: 1'
    ].join('; ');

    const textEl = document.createElement('span');
    textEl.textContent = config.text;
    textEl.style.cssText = [
      'display: inline-flex',
      'align-items: center',
      'min-width: 0',
      'line-height: 1.2',
      'white-space: nowrap'
    ].join('; ');

    element.appendChild(iconEl);
    element.appendChild(textEl);
  } else {
    const displayText = config.custom.icon
      ? `${config.custom.icon} ${config.text}`
      : themeConfig.icon
        ? `${themeConfig.icon} ${config.text}`
        : config.text;

    element.textContent = displayText;
  }

  // 构建样式
  const baseStyles = {
    position: 'fixed',
    zIndex: config.zIndex,
    fontWeight: 'bold',
    fontSize: config.custom.fontSize || sizeConfig.fontSize,
    color: config.custom.color || themeConfig.color,
    pointerEvents: config.onClick ? 'auto' : 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    borderRadius: borderRadius,
    background: config.custom.background || themeConfig.background,
    boxShadow: `0 4px 20px ${themeConfig.shadowColor}`,
    border: `1px solid ${themeConfig.borderColor || 'transparent'}`,
    cursor: config.onClick ? 'pointer' : 'default',
    userSelect: 'none',
    transition: 'all 0.3s ease-out',
    whiteSpace: 'nowrap',
    ...animationConfig.initial
  };

  if (config.layout === 'compactPill') {
    Object.assign(baseStyles, {
      gap: '8px',
      justifyContent: 'flex-start',
      textAlign: 'left',
      borderRadius: '16px',
      backdropFilter: 'blur(10px)',
      boxShadow: `0 10px 24px ${themeConfig.shadowColor}`
    });
  }

  // 根据形状设置宽高或padding
  if (config.layout === 'compactPill') {
    baseStyles.padding = sizeConfig.padding;
    baseStyles.minWidth = sizeConfig.minWidth || '0';
  } else if (config.shape === 'circle') {
    baseStyles.width = sizeConfig.width;
    baseStyles.height = sizeConfig.height;
  } else {
    baseStyles.padding = sizeConfig.padding;
    baseStyles.minWidth = sizeConfig.width;
  }

  // 合并位置样式
  Object.assign(baseStyles, positionConfig);

  // 应用样式
  element.style.cssText = Object.entries(baseStyles)
    .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
    .join('; ');

  // 添加点击事件
  if (config.onClick) {
    element.addEventListener('click', () => {
      config.onClick();
      // 点击后立即移除
      if (element.parentNode) {
        element.parentNode.removeChild(element);
      }
    });
  }

  // 添加到页面
  document.body.appendChild(element);

  // 触发显示回调
  if (config.onShow) {
    config.onShow(element);
  }

  // 触发动画（使用 requestAnimationFrame 确保样式已应用）
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      // 第一阶段：显示
      Object.entries(animationConfig.show).forEach(([key, value]) => {
        element.style[key] = value;
      });

      // 第二阶段：隐藏（延长停留时间，让用户能看清内容）
      setTimeout(() => {
        element.style.transition = 'all 2s ease-out';  // 从1.5s改为2s，更慢的消失动画
        Object.entries(animationConfig.hide).forEach(([key, value]) => {
          element.style[key] = value;
        });
      }, 1500);  // 从300ms改为1500ms，停留更久
    });
  });

  // 动画结束后移除元素
  setTimeout(() => {
    if (element.parentNode) {
      element.parentNode.removeChild(element);
      
      // 触发隐藏回调
      if (config.onHide) {
        config.onHide();
      }
    }
  }, config.duration);

  // 返回元素引用，允许外部控制
  return {
    element,
    remove: () => {
      if (element.parentNode) {
        element.parentNode.removeChild(element);
      }
    }
  };
}

// ==================== 进度追踪器（多任务堆叠模式） ====================
const progressTracker = {
  container: null,
  tasks: new Map(), // taskId -> { element, hideTimer, isMinimized }
  styleInjected: false,
  IDLE_TIMEOUT: 60 * 1000, // 1分钟无更新后消失

  // 注入样式（只执行一次）
  injectStyles() {
    if (this.styleInjected) return;
    
    const style = document.createElement('style');
    style.textContent = `
      .progress-tracker-container {
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 9999;
        display: flex;
        flex-direction: column;
        gap: 10px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      }
      .progress-tracker-item {
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        transform: translateX(120%);
        opacity: 0;
      }
      .progress-tracker-item.show {
        transform: translateX(0);
        opacity: 1;
      }
      .progress-tracker-item.minimized .progress-tracker-body,
      .progress-tracker-item.minimized .progress-tracker-title {
        display: none;
      }
      .progress-tracker-item.minimized .progress-tracker-content {
        padding: 8px 12px;
        min-width: auto;
      }
      .progress-tracker-item.minimized .progress-tracker-header {
        margin-bottom: 0;
      }
      .progress-tracker-item.minimized .progress-tracker-minimize {
        display: none;
      }
      .progress-tracker-item.minimized .progress-tracker-icon::after {
        content: attr(data-progress);
        margin-left: 6px;
        font-weight: 600;
        color: #1890ff;
      }
      .progress-tracker-item.minimized .progress-tracker-close {
        width: 18px;
        height: 18px;
        font-size: 12px;
      }
      .progress-tracker-content {
        background: #ffffff;
        border-radius: 12px;
        padding: 14px 18px;
        min-width: 220px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.05);
        border: 1px solid rgba(24, 144, 255, 0.2);
      }
      .progress-tracker-header {
        display: flex;
        align-items: center;
        margin-bottom: 10px;
        gap: 6px;
      }
      .progress-tracker-icon {
        font-size: 16px;
      }
      .progress-tracker-title {
        flex: 1;
        font-size: 13px;
        font-weight: 500;
        color: #333;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .progress-tracker-minimize,
      .progress-tracker-close {
        width: 20px;
        height: 20px;
        border: none;
        background: #f0f0f0;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        line-height: 1;
        color: #666;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .progress-tracker-minimize:hover {
        background: #e0e0e0;
        color: #333;
      }
      .progress-tracker-close:hover {
        background: #ffebee;
        color: #f44336;
      }
      .progress-tracker-body {
        display: flex;
        align-items: center;
        gap: 10px;
      }
      .progress-tracker-bar-container {
        flex: 1;
        height: 6px;
        background: #f0f0f0;
        border-radius: 3px;
        overflow: hidden;
      }
      .progress-tracker-bar {
        height: 100%;
        background: linear-gradient(90deg, #1890ff, #36cfc9);
        border-radius: 3px;
        transition: width 0.3s ease;
        width: 0%;
      }
      .progress-tracker-text {
        font-size: 12px;
        font-weight: 600;
        color: #1890ff;
        min-width: 45px;
        text-align: right;
      }
    `;
    document.head.appendChild(style);
    this.styleInjected = true;
  },

  // 创建容器
  createContainer() {
    if (this.container) return;
    
    this.injectStyles();
    
    const container = document.createElement('div');
    container.className = 'progress-tracker-container';
    document.body.appendChild(container);
    this.container = container;
  },

  // 创建单个任务元素
  createTaskElement(taskId) {
    const el = document.createElement('div');
    el.className = 'progress-tracker-item';
    el.setAttribute('data-task-id', taskId);
    el.innerHTML = `
      <div class="progress-tracker-content">
        <div class="progress-tracker-header">
          <span class="progress-tracker-icon">📋</span>
          <span class="progress-tracker-title"></span>
          <button class="progress-tracker-minimize" title="最小化">−</button>
          <button class="progress-tracker-close" title="关闭">✕</button>
        </div>
        <div class="progress-tracker-body">
          <div class="progress-tracker-bar-container">
            <div class="progress-tracker-bar"></div>
          </div>
          <span class="progress-tracker-text"></span>
        </div>
      </div>
    `;

    // 绑定事件
    el.querySelector('.progress-tracker-minimize').addEventListener('click', (e) => {
      e.stopPropagation();
      this.minimizeTask(taskId);
    });
    
    el.querySelector('.progress-tracker-close').addEventListener('click', (e) => {
      e.stopPropagation();
      this.removeTask(taskId);
    });
    
    el.addEventListener('click', () => {
      const task = this.tasks.get(taskId);
      if (task && task.isMinimized) {
        this.expandTask(taskId);
      }
    });

    return el;
  },

  // 更新进度
  update(notification) {
    this.createContainer();

    const { taskId, taskName, currentProgress, targetProgress } = notification;
    const percent = Math.round((currentProgress / targetProgress) * 100);
    const progressText = `${currentProgress}/${targetProgress}`;

    let task = this.tasks.get(taskId);
    
    // 如果任务不存在，创建新的
    if (!task) {
      const element = this.createTaskElement(taskId);
      this.container.appendChild(element);
      
      task = {
        element,
        hideTimer: null,
        isMinimized: false,
        minimizeTimer: null
      };
      this.tasks.set(taskId, task);
      
      // 触发显示动画
      requestAnimationFrame(() => {
        element.classList.add('show');
      });
      
      // 3秒后自动最小化
      task.minimizeTimer = setTimeout(() => {
        this.minimizeTask(taskId);
      }, 3000);
    } else {
      // 已存在的任务，如果是最小化状态收到更新，保持最小化
      // 如果是展开状态，重置最小化定时器
      if (!task.isMinimized) {
        if (task.minimizeTimer) {
          clearTimeout(task.minimizeTimer);
        }
        task.minimizeTimer = setTimeout(() => {
          this.minimizeTask(taskId);
        }, 3000);
      }
    }

    // 更新内容
    const el = task.element;
    el.querySelector('.progress-tracker-title').textContent = taskName;
    el.querySelector('.progress-tracker-bar').style.width = `${percent}%`;
    el.querySelector('.progress-tracker-text').textContent = progressText;
    el.querySelector('.progress-tracker-icon').setAttribute('data-progress', progressText);

    // 重置1分钟超时
    this.resetHideTimer(taskId);
  },

  // 最小化任务
  minimizeTask(taskId) {
    const task = this.tasks.get(taskId);
    if (!task) return;
    
    task.isMinimized = true;
    task.element.classList.add('minimized');
    task.element.style.cursor = 'pointer';
    
    if (task.minimizeTimer) {
      clearTimeout(task.minimizeTimer);
      task.minimizeTimer = null;
    }
  },

  // 展开任务
  expandTask(taskId) {
    const task = this.tasks.get(taskId);
    if (!task) return;
    
    task.isMinimized = false;
    task.element.classList.remove('minimized');
    task.element.style.cursor = 'default';
    
    // 3秒后再次最小化
    if (task.minimizeTimer) {
      clearTimeout(task.minimizeTimer);
    }
    task.minimizeTimer = setTimeout(() => {
      this.minimizeTask(taskId);
    }, 3000);
  },

  // 移除任务
  removeTask(taskId) {
    const task = this.tasks.get(taskId);
    if (!task) return;
    
    // 清理定时器
    if (task.hideTimer) {
      clearTimeout(task.hideTimer);
    }
    if (task.minimizeTimer) {
      clearTimeout(task.minimizeTimer);
    }
    
    // 隐藏动画
    task.element.classList.remove('show');
    
    // 动画结束后移除元素
    setTimeout(() => {
      if (task.element.parentNode) {
        task.element.parentNode.removeChild(task.element);
      }
      this.tasks.delete(taskId);
      
      // 如果没有任务了，移除容器
      if (this.tasks.size === 0 && this.container) {
        this.container.parentNode.removeChild(this.container);
        this.container = null;
      }
    }, 300);
  },

  // 重置隐藏定时器
  resetHideTimer(taskId) {
    const task = this.tasks.get(taskId);
    if (!task) return;
    
    if (task.hideTimer) {
      clearTimeout(task.hideTimer);
    }
    task.hideTimer = setTimeout(() => {
      this.removeTask(taskId);
    }, this.IDLE_TIMEOUT);
  },

  // 隐藏所有
  hideAll() {
    for (const taskId of this.tasks.keys()) {
      this.removeTask(taskId);
    }
  },

  // 销毁
  destroy() {
    this.hideAll();
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    this.container = null;
    this.tasks.clear();
  }
};

// 便捷方法
export const FloatingNotification = {
  // 显示经验值
  exp: (amount) => createFloatingNotification({
    text: `+${amount} EXP`,
    theme: 'exp',
    layout: 'compactPill',
    shape: 'rounded',
    size: 'compact',
    position: 'middleRight',
    animation: 'floatUp',
    duration: 2600
  }),

  // 显示连续学习
  streak: (days) => createFloatingNotification({
    text: `连续学习 ${days} 天`,
    theme: 'streak',
    layout: 'compactPill',
    shape: 'rounded',
    size: 'compact',
    position: 'middleRight',
    animation: 'floatUp',
    duration: 2800
  }),

  // 显示成功消息
  success: (message) => createFloatingNotification({
    text: message,
    theme: 'success',
    shape: 'rounded',
    animation: 'fadeIn',
    duration: 3500  // 增加持续时间
  }),

  // 显示警告消息
  warning: (message) => createFloatingNotification({
    text: message,
    theme: 'warning',
    shape: 'rounded',
    animation: 'fadeIn',
    duration: 4000  // 增加持续时间
  }),

  // 显示错误消息
  error: (message) => createFloatingNotification({
    text: message,
    theme: 'error',
    shape: 'rounded',
    animation: 'fadeIn',
    duration: 3500
  }),

  // 显示信息消息
  info: (message) => createFloatingNotification({
    text: message,
    theme: 'info',
    shape: 'rounded',
    animation: 'fadeIn',
    duration: 3500
  }),

  // 显示升级消息
  levelUp: (level) => createFloatingNotification({
    text: `升级到 Lv.${level}`,
    theme: 'levelUp',
    size: 'large',
    position: 'center',
    animation: 'bounce',
    duration: 4500  // 增加持续时间
  }),

  // 自定义通知
  custom: (options) => createFloatingNotification(options),

  // 进度追踪通知（持久化，支持多任务堆叠）
  progress: (notification) => progressTracker.update(notification),

  // 隐藏所有进度追踪器
  hideProgress: () => progressTracker.hideAll(),

  // 移除指定任务的进度追踪器
  removeProgress: (taskId) => progressTracker.removeTask(taskId)
};

// 默认导出
export default FloatingNotification;
